import React, { useEffect, useState, useCallback } from "react";
import { PartyPopper, Gavel, FileDown, X, Layers, ClipboardCheck, Clock, CheckCircle2, Mail, Download, Users, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, openFileById } from "@/lib/api";
import OfficerShell from "./OfficerShell";
import TehsilOfficeDashboard from "./TehsilOfficeDashboard";
import { TEH_LABEL } from "./SdoDashboard";
import { PRELIM_Q, docsForQ, FileChip, WardInfo } from "./TehsildarDashboard";
import { CHECKLIST } from "./CircleDashboard";
import ResultTab from "./ResultTab";
import FeePaymentsView from "@/components/FeePaymentsView";

export const lbName = (a) => (a.data?.village || a.data?.city || "अनिर्दिष्ट");
export const circleName = (a) => {
  const c = a.data?.circle || "";
  return c.includes("::") ? c.split("::").pop() : (c || "—");
};
export const applicant = (a) => (a.data?.nameMr || a.applicantName || "—");
const tehOpinion = (a) => (a.prelimVerification?.decision || a.prelimDecision || "—");

// अंतिमतः पात्र = RDC ने पात्र केलेले किंवा तहसीलदारांनी थेट पात्र ठरवलेले
export const isFinalEligible = (a) =>
  a.rdcDecision === "पात्र" ||
  (a.prelimDecision === "पात्र" && !a.rdcDecision && a.status !== "REJECTED");
// अंतिमतः अपात्र = RDC ने अपात्र (नामंजूर) केलेले
export const isFinalIneligible = (a) => a.rdcDecision === "अपात्र";
export const finalDecision = (a) => (isFinalEligible(a) ? "पात्र" : "अपात्र");

const fmtDT = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

// ---- अंतिम निर्णय form (तहसीलदार form सारखे) ----
function RdcReviewForm({ appId, onDone, readOnly, mode = "decide" }) {
  const [app, setApp] = useState(null);
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.officerDetail(appId).then((a) => { if (alive) setApp(a); }).catch(() => {});
    return () => { alive = false; };
  }, [appId]);

  if (!app) return <div className="py-10 text-center text-slate-400">लोड होत आहे...</div>;
  const d = app.data || {};
  const pv = app.prelimVerification || {};
  const q = pv.questions || pv;
  const current = app.rdcDecision || finalDecision(app);
  // in flip mode show only the opposite action
  const flipAction = current === "पात्र" ? "reject" : "approve";

  const act = async (action) => {
    if (action === "reject" && !remark.trim()) {
      setErr("अर्ज नाकारण्यासाठी शेरा / कारण नमूद करणे आवश्यक आहे."); return;
    }
    setBusy(true); setErr("");
    try {
      await api.rdcReview(appId, { action, remark });
      toast.success(action === "approve"
        ? `अर्ज क्र. ${app.applicationNumber} अंतिम पात्र (मंजूर) केला.`
        : `अर्ज क्र. ${app.applicationNumber} अंतिम अपात्र (नामंजूर) केला.`);
      onDone && onDone();
    } catch (e) { setErr(e.message || "कृती अयशस्वी."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="rdc-review-form">
      {/* header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
        <div>
          <p className="text-[13px] text-slate-500">अर्ज क्रमांक</p>
          <p className="text-[17px] font-bold text-[#1b5e3f]" data-testid="rdc-app-number">{app.applicationNumber}</p>
        </div>
        <div className="text-right">
          <p className="text-[13px] font-semibold text-slate-700">{d.nameMr || app.applicantName}</p>
          <p className="text-[12.5px] text-slate-500">{lbName(app)}<WardInfo d={d} testId="rdc-ward-info" /> · {TEH_LABEL[app.tehsil] || app.tehsil}</p>
        </div>
      </div>

      <p className="mb-3 text-center text-[14px] font-bold text-[#1b5e3f]">प्राथमिक पडताळणी नमुना (तहसीलदार)</p>

      {/* 6 प्रश्न + कागदपत्रे + तहसीलदार होय/नाही उत्तर (alternating shades) */}
      <div className="space-y-3">
        {PRELIM_Q.map((qt, i) => {
          const a = q[`q${i + 1}`];
          const qRemark = q[`q${i + 1}_remark`] || q[`q${i + 1}Remark`] || "";
          return (
            <div key={i} data-testid={`rdc-prelim-q-${i + 1}`}
              className={`rounded-lg border border-slate-200 p-3 ${i % 2 === 0 ? "bg-[#f7f3e8]" : "bg-white"}`}>
              <p className="text-[13.5px] text-slate-700">{i + 1}. {qt}</p>
              <div className="mt-2.5 flex flex-wrap gap-2">
                {docsForQ(i, d).map((doc, k) => <FileChip key={k} file={doc.file} label={doc.label} testId={doc.testId} />)}
              </div>
              <div className="mt-2">
                <span className={`inline-block rounded-full px-3 py-1 text-[12.5px] font-bold ${a === "होय" ? "bg-emerald-100 text-emerald-700" : a === "नाही" ? "bg-red-100 text-red-600" : "bg-slate-100 text-slate-500"}`}>
                  तहसीलदार उत्तर: {a || "—"}
                </span>
                {qRemark && <p className="mt-1.5 text-[12.5px] text-slate-600"><span className="font-semibold">तहसीलदार शेरा:</span> {qRemark}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* तहसीलदार एकत्रित अभिप्राय + शेरा */}
      <div className="mt-4 rounded-xl bg-red-50 p-3.5 ring-1 ring-red-200" data-testid="rdc-teh-opinion">
        <p className="text-[13px] font-bold text-red-700">तहसीलदार यांचा अभिप्राय: {tehOpinion(app)}</p>
        {pv.remark && <p className="mt-1 text-[13px] text-slate-700"><span className="font-semibold">तहसीलदार शेरा:</span> {pv.remark}</p>}
      </div>

      {/* SDO अभिप्राय + शेरा (लागू असल्यास) */}
      {app.sdoDecision && (
        <div className="mt-3 rounded-xl bg-amber-50 p-3.5 ring-1 ring-amber-200" data-testid="rdc-sdo-opinion">
          <p className="text-[13px] font-bold text-amber-800">उपविभागीय अधिकारी यांचा अभिप्राय: {app.sdoDecision}</p>
          {app.sdoRemark && <p className="mt-1 text-[13px] text-slate-700"><span className="font-semibold">SDO शेरा:</span> {app.sdoRemark}</p>}
        </div>
      )}

      {/* RDC निर्णय नोंद (override/flip history) */}
      {(() => {
        const rdcLog = (app.history || []).filter((h) => h.action === "rdc_review");
        if (!rdcLog.length) return null;
        return (
          <div className="mt-3 rounded-xl bg-slate-50 p-3.5 ring-1 ring-slate-200" data-testid="rdc-decision-log">
            <p className="mb-2 text-[13px] font-bold text-slate-700">RDC निर्णय नोंद ({rdcLog.length})</p>
            <ul className="space-y-2">
              {rdcLog.slice().reverse().map((h, i) => {
                const dec = h.decision || (h.status === "REJECTED" ? "अपात्र" : "पात्र");
                const note = h.note || h.remark || "";
                return (
                  <li key={i} className="rounded-lg bg-white p-2.5 text-[12.5px] ring-1 ring-slate-100" data-testid={`rdc-log-entry-${i}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {h.isFlip && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700" data-testid="rdc-log-flip-badge">निर्णय बदल</span>}
                      <span className={`font-bold ${dec === "पात्र" ? "text-emerald-700" : "text-red-600"}`}>{dec}</span>
                      {h.isFlip && h.previousDecision && <span className="text-slate-400">({h.previousDecision} → {dec})</span>}
                      <span className="ml-auto text-[11.5px] text-slate-400">{fmtDT(h.at)}</span>
                    </div>
                    {note && <p className="mt-1 text-slate-600"><span className="font-semibold">कारण:</span> {note}</p>}
                    {h.by && <p className="text-[11.5px] text-slate-400">— {h.by}</p>}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {readOnly ? (
        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-[14px] font-bold text-[#1b5e3f]">अंतिम निर्णय:{" "}
            <span className={current === "पात्र" ? "text-emerald-700" : "text-red-600"}>{current}</span>
          </p>
          {app.rdcRemark && <p className="mt-1 text-[13px] text-slate-600"><span className="font-semibold">शेरा:</span> {app.rdcRemark}</p>}
        </div>
      ) : (
        <div className="mt-5 border-t border-slate-100 pt-4">
          {mode === "flip" && (
            <p className="mb-2 text-[13px] text-slate-600">सध्याचा निर्णय:{" "}
              <span className={`font-bold ${current === "पात्र" ? "text-emerald-700" : "text-red-600"}`}>{current}</span>
              {" "}— खालील बटणाने निर्णय बदलता येईल.
            </p>
          )}
          <p className="mb-2 text-[14px] font-bold text-[#1b5e3f]">निवासी उपजिल्हाधिकारी यांचा अंतिम निर्णय</p>
          <label className="mb-1.5 block text-[13px] font-semibold text-slate-600">शेरा / कारण{" "}
            {flipAction === "reject" && mode === "flip"
              ? <span className="text-red-500">* (नामंजूरसाठी आवश्यक)</span>
              : mode === "decide"
                ? <span className="text-slate-400">(नामंजूर केल्यास आवश्यक)</span>
                : <span className="text-slate-400">(ऐच्छिक)</span>}
          </label>
          <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} data-testid="rdc-remark"
            placeholder="शेरा / कारण नमूद करा" className="mb-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f]" />
          {err && <p className="mb-2 text-[13px] text-red-600" data-testid="rdc-error">{err}</p>}
          <div className="flex flex-wrap gap-3">
            {mode === "flip" ? (
              flipAction === "approve" ? (
                <button type="button" disabled={busy} onClick={() => act("approve")} data-testid="rdc-flip-approve-btn"
                  className="rounded-lg bg-[#5a9e3a] px-6 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  मंजूर करा (पात्र मध्ये बदला)
                </button>
              ) : (
                <button type="button" disabled={busy} onClick={() => act("reject")} data-testid="rdc-flip-reject-btn"
                  className="rounded-lg bg-red-600 px-6 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  नामंजूर करा (अपात्र मध्ये बदला)
                </button>
              )
            ) : (
              <>
                <button type="button" disabled={busy} onClick={() => act("approve")} data-testid="rdc-approve-btn"
                  className="rounded-lg bg-[#5a9e3a] px-6 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  अंतिम मंजूर (पात्र)
                </button>
                <button type="button" disabled={busy} onClick={() => act("reject")} data-testid="rdc-reject-btn"
                  className="rounded-lg bg-red-600 px-6 py-2.5 text-[14px] font-semibold text-white hover:opacity-90 disabled:opacity-60">
                  अंतिम नामंजूर (अपात्र)
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCards({ items }) {
  return (
    <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {items.map((s, i) => (
        <div key={i} className="rounded-xl border border-[#e6dcc4] bg-white p-4">
          <s.icon className={`mb-1.5 h-5 w-5 ${s.color}`} />
          <p className={`text-[24px] font-bold ${s.color}`}>{s.value}</p>
          <p className="text-[12.5px] text-slate-500">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

function TehsilTabs({ tehsils, tab, setTab }) {
  return (
    <div className="mb-5 flex flex-wrap gap-2">
      {tehsils.map((t) => (
        <button key={t} onClick={() => setTab(t)} data-testid={`rdc-taluka-tab-${t}`}
          className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${tab === t ? "bg-[#c2570a] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>
          {TEH_LABEL[t] || t}
        </button>
      ))}
    </div>
  );
}

// ---- प्राथमिक पडताळणी (RDC): stat cards + तहसील निवड + यादी + निर्णय ----
function RdcReviewTab({ tehsils, readOnly = false }) {
  const [allApps, setAllApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("");
  const [openId, setOpenId] = useState(null);

  useEffect(() => { if (tehsils.length && !tab) setTab(tehsils[0]); }, [tehsils, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.officerList("", "", "");
      setAllApps(list || []);
    } catch (e) { /* noop */ }
    setLoading(false);
    setOpenId(null);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return (
      <div>
        <button onClick={() => setOpenId(null)} data-testid="rdc-back-list"
          className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">← यादीकडे परत</button>
        <RdcReviewForm key={openId} appId={openId} onDone={load} readOnly={readOnly} />
      </div>
    );
  }

  const pendingAll = allApps.filter((a) => a.forwardedToRDC)
    .sort((a, b) => new Date(a.submittedAt || a.createdAt || 0) - new Date(b.submittedAt || b.createdAt || 0));
  const decidedCount = allApps.filter((a) => a.rdcDecision).length;
  const stats = [
    { icon: Layers, label: "RDC कडे आलेले अर्ज", value: pendingAll.length + decidedCount, color: "text-[#1b5e3f]" },
    { icon: CheckCircle2, label: "अंतिम निर्णय झालेले", value: decidedCount, color: "text-emerald-600" },
    { icon: Clock, label: "अंतिम निर्णयासाठी प्रलंबीत", value: pendingAll.length, color: "text-amber-600" },
    { icon: ClipboardCheck, label: "तालुके", value: tehsils.length, color: "text-[#c2570a]" },
  ];
  if (readOnly) return <div><StatCards items={stats} /></div>;
  const filtered = readOnly ? pendingAll : pendingAll.filter((a) => a.tehsil === tab);

  return (
    <div>
      <StatCards items={stats} />
      {!readOnly && <TehsilTabs tehsils={tehsils} tab={tab} setTab={setTab} />}

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">{readOnly ? "उपविभागीय अधिकारी यांच्याकडून प्राप्त अर्ज (अंतिम निर्णयासाठी)" : `${TEH_LABEL[tab] || tab} तहसील — उपविभागीय अधिकारी यांच्याकडून प्राप्त अर्ज (अंतिम निर्णयासाठी)`}</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="rdc-verify-empty">
            <PartyPopper className="mx-auto mb-2 h-8 w-8 text-emerald-600" />
            <p className="text-[14px] font-semibold text-emerald-700">या तहसीलमध्ये अंतिम निर्णयासाठी प्रलंबीत अर्ज नाहीत.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="rdc-verify-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्थेचे नाव</th>
                  <th className="p-2.5 font-semibold">अर्ज क्रमांक</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 font-semibold">तहसीलदार यांचा अभिप्राय</th>
                  <th className="p-2.5 font-semibold">उपविभागीय अधिकारी यांचा अभिप्राय</th>
                  <th className="p-2.5 text-center font-semibold">निर्णय</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{lbName(a)}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}<br /><span className="text-[12px] text-slate-400">{a.data?.mobile}</span></td>
                    <td className="p-2.5"><span className={tehOpinion(a) === "पात्र" ? "font-semibold text-emerald-700" : "font-semibold text-red-600"}>{tehOpinion(a)}</span></td>
                    <td className="p-2.5"><span className={a.sdoDecision === "पात्र" ? "font-semibold text-emerald-700" : "font-semibold text-red-600"}>{a.sdoDecision || "—"}</span></td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setOpenId(a.id)} data-testid={`rdc-decide-${a.id}`}
                        className={`inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90 ${readOnly ? "bg-[#1b5e3f]" : "bg-[#c2570a]"}`}>
                        <Gavel className="h-3.5 w-3.5" /> {readOnly ? "पहा" : "निर्णय"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- अंतिम निर्णय झालेले अर्ज ----
export function exportExcel(rows, label, tehsilLabel) {
  const head = ["अ.क्र.", "तहसील", "स्थानिक स्वराज्य संस्थेचे नाव", "अर्जदाराचे नाव", "मोबाईल", "अर्ज क्रमांक", "निर्णय"];
  const body = rows.map((a, i) => [i + 1, TEH_LABEL[a.tehsil] || a.tehsil || "", lbName(a), applicant(a), a.data?.mobile || "", a.applicationNumber || "", finalDecision(a)]);
  const trs = [head, ...body].map((r) => "<tr>" + r.map((c) => `<td>${String(c ?? "")}</td>`).join("") + "</tr>").join("");
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"/></head><body><table border="1">${trs}</table></body></html>`;
  const blob = new Blob(["\ufeff", html], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `अंतिम-निर्णय-${label}-${tehsilLabel}.xls`;
  link.click();
  URL.revokeObjectURL(url);
}

function RdcFinalTab({ tehsils }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState("पात्र");
  const [tab, setTab] = useState("");
  const [viewId, setViewId] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { if (tehsils.length && !tab) setTab(tehsils[0]); }, [tehsils, tab]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.officerList("", "", "");
      setRows((list || []).filter((a) => isFinalEligible(a) || isFinalIneligible(a)));
    } catch (e) { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendPhysical = async () => {
    setSending(true);
    try {
      const r = await api.rdcSendPhysical(tab);
      toast.success(`${TEH_LABEL[tab] || tab}: ${r.sent} पात्र अर्ज पायाभूत सुविधा तपासणीसाठी पाठवले.`);
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setSending(false);
  };

  const filtered = rows.filter((a) => a.tehsil === tab && (sub === "पात्र" ? isFinalEligible(a) : isFinalIneligible(a)));
  const talukaEligible = rows.filter((a) => a.tehsil === tab && isFinalEligible(a));
  const feeBlockedApps = talukaEligible.filter((a) => a.paymentStatus === "rejected");
  const feeBlocked = feeBlockedApps.length > 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["पात्र", "अपात्र"].map((s) => (
          <button key={s} onClick={() => setSub(s)} data-testid={`rdc-final-subtab-${s === "पात्र" ? "eligible" : "ineligible"}`}
            className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${sub === s ? (s === "पात्र" ? "bg-[#5a9e3a] text-white" : "bg-red-600 text-white") : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>
            {s} अर्ज
          </button>
        ))}
        <div className="ml-auto flex flex-wrap gap-2">
          {sub === "पात्र" && (
            <button onClick={sendPhysical} disabled={sending || filtered.length === 0 || feeBlocked} data-testid="rdc-send-physical"
              title={feeBlocked ? "शुल्क पडताळणीत नाकारलेले पात्र अर्ज आधी निकाली काढा" : ""}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#c2570a] px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50">
              <ClipboardCheck className="h-4 w-4" /> {sending ? "पाठवत आहे..." : "पायाभूत सुविधा तपासणीसाठी पाठवा"}
            </button>
          )}
          <button onClick={() => exportExcel(filtered, sub, TEH_LABEL[tab] || tab)} disabled={filtered.length === 0} data-testid="rdc-final-excel"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
            <FileDown className="h-4 w-4" /> अंतिम करा (Excel)
          </button>
        </div>
      </div>

      <TehsilTabs tehsils={tehsils} tab={tab} setTab={setTab} />

      {sub === "पात्र" && feeBlocked && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-red-50 p-3 text-[13px] text-red-700 ring-1 ring-red-200" data-testid="rdc-fee-block-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>या तालुक्यातील <b>{feeBlockedApps.length}</b> पात्र अर्ज शुल्क पडताळणीत नाकारले आहेत. जोपर्यंत हे निकाली निघत नाहीत (शुल्क पुन्हा verify होत नाही किंवा अर्ज अपात्र होत नाही) तोपर्यंत या तालुक्याचे एकही पात्र अर्ज पायाभूत सुविधा तपासणीसाठी पाठवता येणार नाहीत.</span>
        </div>
      )}

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-3 text-[15px] font-bold text-[#1b5e3f]">{TEH_LABEL[tab] || tab} तहसील — {sub} अर्ज (अंतिम निर्णय)</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-slate-400" data-testid="rdc-final-empty">या तहसीलमध्ये {sub} प्रकारात अंतिम निर्णय झालेले अर्ज नाहीत.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="rdc-final-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्थेचे नाव</th>
                  <th className="p-2.5 font-semibold">अर्ज क्रमांक</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 font-semibold">निर्णय</th>
                  <th className="p-2.5 text-center font-semibold">पाहा</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{lbName(a)}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}</td>
                    <td className="p-2.5"><span className={finalDecision(a) === "पात्र" ? "font-semibold text-emerald-700" : "font-semibold text-red-600"}>{finalDecision(a)}</span>
                      {a.paymentStatus === "rejected" && (
                        <span data-testid={`fee-rejected-flag-${a.id}`} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10.5px] font-bold text-red-600">
                          <AlertTriangle className="h-3 w-3" /> शुल्क नाकारले
                        </span>
                      )}
                    </td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setViewId(a.id)} data-testid={`rdc-view-${a.id}`}
                        className="rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33]">पाहा</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {viewId && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="rdc-view-modal">
          <div className="relative my-6 w-full max-w-3xl">
            <button onClick={() => setViewId(null)} data-testid="rdc-view-close"
              className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow ring-1 ring-slate-200 hover:bg-slate-50"><X className="h-5 w-5" /></button>
            <RdcReviewForm key={viewId} appId={viewId} mode="flip" onDone={() => { setViewId(null); load(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ---- पायाभूत सुविधा तपासणी अहवाल (RDC) ----
function CircleReportView({ report, app }) {
  const r = report || {};
  const d = app?.data || {};
  const photo = (p) => p?.fileId ? (
    <button type="button" onClick={() => openFileById(p.fileId)} className="text-[12px] text-[#c2570a] underline">फोटो पहा</button>
  ) : <span className="text-slate-300">—</span>;
  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="rdc-circle-report">
      <p className="mb-1 text-center text-[15px] font-bold text-[#1b5e3f]">मंडळ अधिकारी — पायाभूत सुविधा तपासणी अहवाल</p>
      {app && (
        <p className="mb-3 text-center text-[12.5px] text-slate-500">
          अर्ज क्र. {app.applicationNumber} · {d.nameMr || app.applicantName || ""} · {d.village || d.city || ""} {d.mobile ? "· " + d.mobile : ""}
        </p>
      )}
      <div className={`mb-4 rounded-xl p-3 text-center text-[14px] font-bold ${r.recommendation === "बदल" ? "bg-amber-50 text-amber-800 ring-1 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"}`}>
        एकंदर अभिप्राय: {r.recommendation === "बदल" ? "अर्जदाराने दिलेल्या माहितीमध्ये बदल आहे" : "अर्जदाराने दिलेली माहिती योग्य आहे"}
        {r.remark && <div className="mt-1 text-[12.5px] font-normal">शेरा: {r.remark}</div>}
      </div>
      <div className="mb-4 grid gap-x-8 gap-y-1 rounded-xl bg-[#faf8f0] p-3 md:grid-cols-2">
        {[["पडताळणी अधिकारी", r.officerName], ["पदनाम", r.designation], ["स्थळ पाहणी दिनांक", r.inspectionDate], ["वेळ", r.inspectionTime]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-3 border-b border-slate-100 py-1"><span className="text-[12.5px] text-slate-500">{k}</span><span className="text-[13px] font-medium text-slate-800">{v || "—"}</span></div>
        ))}
        <div className="flex justify-between gap-3 py-1"><span className="text-[12.5px] text-slate-500">बाहेरील फोटो</span>{photo(r.photoOutside)}</div>
        <div className="flex justify-between gap-3 py-1"><span className="text-[12.5px] text-slate-500">आतील फोटो</span>{photo(r.photoInside)}</div>
      </div>
      {Array.isArray(r.checklist) && r.checklist.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead><tr className="bg-slate-100 text-left text-slate-600"><th className="p-2">अ.क्र.</th><th className="p-2">मुद्दा</th><th className="p-2">अर्जदाराने नमूद केलेला तपशील</th><th className="p-2 text-center">प्रत्यक्ष पडताळणी</th></tr></thead>
            <tbody>
              {r.checklist.map((c) => (
                <tr key={c.n} className="border-b border-slate-100 align-top">
                  <td className="p-2">{c.n}</td>
                  <td className="p-2">{CHECKLIST[c.n - 1] || c.label}</td>
                  <td className="p-2 text-slate-600">{c.applicantDetail || "—"}</td>
                  <td className="p-2 text-center"><span className={!c.verify ? "text-slate-400" : c.verify === "नाही" ? "font-semibold text-red-600" : "font-semibold text-emerald-700"}>{c.verify || "—"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {app?.tehsildarConfirmed && (
        <div className="mt-4 rounded-xl bg-[#eef6f0] p-3 ring-1 ring-emerald-100" data-testid="rdc-tehsildar-remark">
          <p className="text-[13px] font-bold text-[#1b5e3f]">तहसीलदार खात्री ✓{app.tehsildarConfirmedBy ? ` — ${app.tehsildarConfirmedBy}` : ""}{app.tehsildarConfirmedAt ? ` · ${String(app.tehsildarConfirmedAt).slice(0, 10)}` : ""}</p>
          <p className="mt-1 text-[12.5px] text-slate-700">
            <span className="font-semibold">शेरा:</span> {app.tehsildarConfirmRemark ? app.tehsildarConfirmRemark : "मंडळ अधिकारी अहवालास खात्री दिली (कोणताही अतिरिक्त शेरा नाही)."}
          </p>
        </div>
      )}
    </div>
  );
}

function RdcPhysicalTab({ tehsils, readOnly = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("");
  const [view, setView] = useState(null);
  const [sendingMark, setSendingMark] = useState(false);

  useEffect(() => { if (tehsils.length && !tab) setTab(tehsils[0]); }, [tehsils, tab]);
  const load = useCallback(() => {
    setLoading(true);
    api.rdcPhysicalList().then((d) => setRows(d || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = rows.filter((a) => a.visitDone).length;
  const stats = [
    { icon: Layers, label: "तपासणीसाठी पाठवलेले", value: rows.length, color: "text-[#1b5e3f]" },
    { icon: CheckCircle2, label: "तपासणी झालेले", value: done, color: "text-emerald-600" },
    { icon: Clock, label: "तपासणी प्रलंबीत", value: rows.length - done, color: "text-amber-600" },
    { icon: ClipboardCheck, label: "तालुके", value: tehsils.length, color: "text-[#c2570a]" },
  ];
  if (readOnly) return <div><StatCards items={stats} /></div>;
  const filtered = rows.filter((a) => a.tehsil === tab);
  const allVisitsDone = filtered.length > 0 && filtered.every((a) => a.visitDone);
  const allSentToMark = filtered.length > 0 && filtered.every((a) => a.sentToMarking);

  const sendMarking = async () => {
    setSendingMark(true);
    try {
      const r = await api.rdcSendMarking(tab);
      toast.success(`${TEH_LABEL[tab] || tab}: ${r.sent} पात्र अर्ज गुणांकनासाठी तहसीलदार व SDO कडे पाठवले.`);
      load();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setSendingMark(false);
  };

  return (
    <div>
      <StatCards items={stats} />
      {!readOnly && <TehsilTabs tehsils={tehsils} tab={tab} setTab={setTab} />}
      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[15px] font-bold text-[#1b5e3f]">{`${TEH_LABEL[tab] || tab} तहसील — मंडळ अधिकारी पायाभूत सुविधा तपासणी अहवाल`}</p>
          <button onClick={sendMarking} disabled={sendingMark || !allVisitsDone || allSentToMark} data-testid="rdc-send-marking"
            title={!allVisitsDone ? "सर्व मंडळ अधिकाऱ्यांची पायाभूत तपासणी पूर्ण झाल्यावरच सक्रिय होईल" : allSentToMark ? "या तालुक्याचे अर्ज आधीच गुणांकनासाठी पाठवले आहेत" : ""}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
            <Send className="h-4 w-4" /> {sendingMark ? "पाठवत आहे..." : allSentToMark ? "गुणांकनासाठी पाठवले ✓" : "गुणांकनासाठी पाठवा"}
          </button>
        </div>
        {!allVisitsDone && filtered.length > 0 && (
          <p className="mb-3 text-[12.5px] text-amber-700" data-testid="rdc-marking-gate-note">सर्व मंडळ अधिकाऱ्यांची पायाभूत सुविधा तपासणी पूर्ण झाल्यावरच "गुणांकनासाठी पाठवा" सक्रिय होईल.</p>
        )}
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-slate-400" data-testid="rdc-physical-empty">या तहसीलमध्ये तपासणीसाठी पाठवलेले अर्ज नाहीत.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="rdc-physical-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्थेचे नाव</th>
                  <th className="p-2.5 font-semibold">मंडळाचे नाव</th>
                  <th className="p-2.5 font-semibold">अर्ज क्रमांक</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 font-semibold">मंडळ अधिकारी यांचा अहवाल</th>
                  <th className="p-2.5 text-center font-semibold">पहा</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{lbName(a)}</td>
                    <td className="p-2.5 text-slate-700">{circleName(a)}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}</td>
                    <td className="p-2.5">{a.visitDone ? <span className={a.visitDecision === "बदल" ? "font-semibold text-amber-700" : "font-semibold text-emerald-700"}>{a.visitDecision === "बदल" ? "बदल आहे" : "योग्य"}</span> : <span className="text-slate-400">प्रलंबीत</span>}</td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setView(a)} disabled={!a.visitDone} data-testid={`rdc-physical-view-${a.id}`}
                        className="rounded-full bg-[#1b5e3f] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-40">पहा</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {view && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" data-testid="rdc-physical-modal">
          <div className="relative my-6 w-full max-w-3xl">
            <button onClick={() => setView(null)} className="absolute -top-3 -right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow ring-1 ring-slate-200 hover:bg-slate-50"><X className="h-5 w-5" /></button>
            <CircleReportView report={view.visitReport} app={view} />
          </div>
        </div>
      )}
    </div>
  );
}


// ---- गुणांकन (RDC) — १०० गुण ----
export const MARKING_SECTIONS = [
  { title: "१. शैक्षणिक पात्रता", items: [
    ["१२ वी (HSC) उत्तीर्ण", 5], ["पदवी", 2], ["पदव्युत्तर पदवी", 3]] },
  { title: "२. संगणक व डिजिटल साक्षरता", items: [
    ["MSCIT किंवा समकक्ष प्रमाणपत्र", 7], ["सायबर सुरक्षाबाबतचे प्रमाणपत्र", 4], ["ऑनलाईन अर्जप्रक्रिया व संगणक हाताळणी बाबतचे किमान 1 वर्ष अनुभवाचे शासकीय विभाग प्रमुखाचे प्रमाणपत्र", 4]] },
  { title: "३. पायाभूत सुविधा", items: [
    ["किमान १०० चौ.फूट क्षेत्रफळाचे स्वतंत्र कार्यालय (८-अ उतारा / असेसमेंट उतारा / भाडेकरार)", 2], ["दोन संगणक / लॅपटॉप", 2], ["लेझर प्रिंटर", 1], ["स्कॅनर", 1],
    ["बायोमेट्रिक उपकरण", 2], ["वेबकॅम", 1], ["यूपीएस अथवा पर्यायी वीज बॅकअप", 1], ["ब्रॉडबँड इंटरनेट (ग्रामीण १० Mbps, नागरी २० Mbps)", 2], ["सीसीटीव्ही व्यवस्था", 1],
    ["नागरिकांसाठी प्रतीक्षा कक्ष व सेवा शुल्क फलक", 1], ["दिव्यांग व ज्येष्ठ नागरिकांसाठी सुलभ प्रवेश", 1]] },
  { title: "४. आर्थिक क्षमता", items: [
    ["पुरेशी बँक शिल्लक / मुदत ठेव (FD) व इतर गुंतवणूक", 4], ["आर्थिक क्षमता ऐपत (बँक सॉल्वन्सी/गॅरेंटी/तहसीलदार दाखला)", 2], ["मागील २ वर्षांची आयकर विवरणपत्रे (2024-25 व 2025-26)", 2], ["आर्थिक शाश्वतता स्वयंघोषणापत्र", 2]] },
  { title: "५. मुलाखतीद्वारे", items: [
    ["शासकीय सेवांचे ज्ञान व नागरिकाभिमुख सेवावृत्ती", 6],
    ["समस्या सोडविण्याची क्षमता, संवाद कौशल्य व प्रामाणिकपणा व नैतिकता", 8],
    ["डिजीटल प्रणाली हाताळणी व माहिती गोपनियता व डेटा सुरक्षा जागरूकता", 6]] },
  { title: "६. अनुभव", items: [
    ["सीएससी ऑपरेटर", 3, 2], ["महा ई सेवा केंद्र", 3, 2], ["बँकिंग प्रतिनिधी (BC)", 2, 1], ["ई-गव्हर्नन्स प्रकल्प (वर्क ऑर्डरसह) / शासकीय सेवा केंद्र कराराचा अनुभव (वर्क ऑर्डरसह)", 2, 1]] },
  { title: "७. व्यवसाय आराखडा व शाश्वतता", items: [
    ["सेवा विस्तार", 2], ["जनजागृती", 1], ["आर्थिक व्यवहार्यता", 1], ["व्यवसाय नियोजन", 1]] },
  { title: "८. सामाजिक समावेशकता (कमाल ५)", items: [
    ["महिला", 5], ["माजी सैनिक", 5], ["दिव्यांग", 5], ["स्वयंसहाय्यता गट सदस्य", 5], ["स्थानिक उद्योजक", 5]], cap: 5 },
  { title: "९. चारित्र्य पडताळणी", items: [
    ["पोलीस पडताळणी (दोषसिद्धी/फसवणूक नसणे)", 8], ["काळ्या यादीत नसणे", 2]] },
];

export const MARK_DOCS = {
  "s0_0": "edu_file_0", "s0_1": "edu_file_1", "s0_2": "edu_file_2",
  "s1_0": "cl_file_0", "s1_1": "cl_file_1", "s1_2": "cl_file_2",
  "s2_0": "infra_file_2",
  "s8_0": "self_declaration_file",
};

// maps each गुणांकन row to the applicant's actual answer field (yes/no)
export const MARK_ANSWERS = {
  "s0_0": "edu_passed_0", "s0_1": "edu_passed_1", "s0_2": "edu_passed_2",
  "s1_0": "cl_passed_0", "s1_1": "cl_passed_1", "s1_2": "cl_passed_2",
  "s2_0": "infra_2", "s2_1": "infra_3", "s2_2": "infra_4", "s2_3": "infra_5", "s2_4": "infra_6",
  "s2_5": "infra_7", "s2_6": "infra_8", "s2_7": "infra_9", "s2_8": "infra_10", "s2_9": "infra_12", "s2_10": "infra_11",
  "s3_0": "fin_plan_0", "s3_1": "fin_plan_1", "s3_2": "fin_plan_2", "s3_3": "fin_plan_3",
  "s5_0": "cscExp", "s5_1": "prevCenter", "s5_2": "bankRep", "s5_3": "egovExp",
  "s6_0": "bp_plan_0", "s6_1": "bp_plan_1", "s6_2": "bp_plan_2", "s6_3": "bp_plan_3",
  "s7_0": "si_female", "s7_1": "si_exservice", "s7_2": "si_disabled", "s7_3": "si_shg", "s7_4": "si_entrepreneur",
  "s8_0": "cv_fraud", "s8_1": "cv_blacklist",
};

// role -> which गुणांकन sections (indices into MARKING_SECTIONS) that role may fill
export const ROLE_MARK_SECTIONS = {
  rdc: [0, 1, 2, 3, 4, 5, 6, 7, 8],
  tehsildar: [0, 1, 2, 7, 8], // गुण १,२,३,८,९
  sdo: [3, 5, 6],             // गुण ४,६,७
  aro: [4],                   // गुण ५ (मुलाखत)
};

function MarkingForm({ appId, onDone, role = "rdc", readOnly = false }) {
  const [app, setApp] = useState(null);
  const [scores, setScores] = useState({});
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.officerDetail(appId).then((a) => {
      setApp(a);
      const m = a.marking || {};
      setScores(m.scores || {});
      setRemark(m.remark || "");
    }).catch(() => {});
  }, [appId]);
  if (!app) return <div className="rounded-2xl bg-white p-10 text-center text-slate-400">लोड होत आहे...</div>;
  const d = app.data || {};
  const isFull = role === "rdc";
  const useButtons = role === "tehsildar" || role === "sdo";
  const secIdx = ROLE_MARK_SECTIONS[role] || ROLE_MARK_SECTIONS.rdc;
  const m0 = app.marking || {};
  const SECTION_OWNER = { 0: "tehsildar", 1: "tehsildar", 2: "tehsildar", 7: "tehsildar", 8: "tehsildar", 3: "sdo", 5: "sdo", 6: "sdo", 4: "aro" };
  const OWNER_LABEL = { tehsildar: "तहसीलदार", sdo: "SDO", aro: "ARO" };
  const ownerTag = (si) => {
    const r = SECTION_OWNER[si];
    if (!r) return null;
    const by = r === "aro" ? m0.interviewBy : m0[`${r}By`];
    const at = r === "aro" ? m0.interviewAt : m0[`${r}At`];
    const done = !!by;
    return (
      <span className={`ml-2 rounded-full px-2 py-0.5 text-[10.5px] font-bold ${done ? "bg-emerald-100 text-emerald-700" : "bg-white/25 text-white"}`}
        title={done ? `${by}${at ? " · " + String(at).slice(0, 10) : ""}` : ""}>
        {OWNER_LABEL[r]} {done ? `✓ ${by}` : "— प्रलंबित"}
      </span>
    );
  };
  const setScore = (key, max, val) => {
    let n = val === "" ? "" : Math.max(0, Math.min(max, Number(val)));
    setScores((s) => ({ ...s, [key]: n }));
  };
  const secTotal = (si, sec) => {
    let t = sec.items.reduce((a, _, ci) => a + (Number(scores[`s${si}_${ci}`]) || 0), 0);
    return sec.cap ? Math.min(sec.cap, t) : t;
  };
  const grand = secIdx.reduce((a, si) => a + secTotal(si, MARKING_SECTIONS[si]), 0);
  const roleMax = secIdx.reduce((a, si) => {
    const sec = MARKING_SECTIONS[si];
    return a + (sec.cap || sec.items.reduce((x, [, m]) => x + m, 0));
  }, 0);
  const save = async () => {
    setBusy(true);
    try {
      if (isFull) {
        await api.rdcMarking(appId, { scores, remark, total: grand });
      } else {
        const sub = {};
        secIdx.forEach((si) => MARKING_SECTIONS[si].items.forEach((_, ci) => {
          const k = `s${si}_${ci}`;
          if (scores[k] !== undefined && scores[k] !== "") sub[k] = scores[k];
        }));
        await api.sectionMarking(appId, { scores: sub, remark, total: grand });
      }
      toast.success(`गुणांकन जतन झाले — ${grand}/${roleMax} गुण.`);
      onDone && onDone();
    } catch (e) { toast.error("त्रुटी: " + (e.message || "")); }
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5" data-testid="rdc-marking-form">
      <p className="text-center text-[15px] font-bold text-[#1b5e3f]">{isFull ? "गुणांकन पत्रक (एकूण १०० गुण)" : "गुणांकन पत्रक — नेमून दिलेले विभाग"}</p>
      <p className="mb-4 text-center text-[12.5px] text-slate-500">अर्ज क्र. {app.applicationNumber} · {d.nameMr || app.applicantName} · {d.village || d.city || ""}<WardInfo d={d} testId="rdc-mirror-ward-info" /> · {TEH_LABEL[app.tehsil] || app.tehsil}</p>
      {secIdx.map((si) => {
        const sec = MARKING_SECTIONS[si];
        const isExp = si === 5;
        return (
        <div key={si} className="mb-4">
          <div className="mb-1 flex items-center justify-between rounded bg-[#1b5e3f] px-3 py-1.5">
            <span className="text-[13.5px] font-bold text-white">{sec.title}{isFull && ownerTag(si)}</span>
            <span className="text-[12.5px] font-semibold text-white">{secTotal(si, sec)} / {sec.cap || sec.items.reduce((a, [, m]) => a + m, 0)}</span>
          </div>
          {isExp && <p className="mb-1 px-1 text-[11.5px] font-medium text-[#c2570a]">अनुभवाच्या कालावधीनुसार गुण द्या — <b>एक वर्षाच्या आतील</b> किंवा <b>एक वर्षाच्या वरील</b>.</p>}
          <table className="w-full text-[13px]">
            <thead><tr className="text-left text-[11.5px] text-slate-400"><th className="p-1">निकष</th><th className="w-24 p-1 text-center">अर्जदाराचे उत्तर</th><th className="w-24 p-1 text-center">संबंधित कागदपत्र</th><th className="w-16 p-1 text-center">कमाल</th><th className={`p-1 text-center ${isExp ? "w-60" : useButtons ? "w-44" : "w-24"}`}>{isExp ? "अनुभव — गुण (आतील / वरील)" : useButtons ? "निकष पूर्ण होतो काय?" : "प्राप्त गुण"}</th></tr></thead>
            <tbody>
              {sec.items.map(([label, max, maxBelow], ci) => {
                const df = d[MARK_DOCS[`s${si}_${ci}`]];
                const af = MARK_ANSWERS[`s${si}_${ci}`];
                const av = af ? d[af] : undefined;
                let ans = av === "yes" ? "होय" : av === "no" ? "नाही" : (av || "—");
                if (si === 2 && av === "yes" && af) {
                  const q = d[af.replace("infra_", "infra_qty_")];
                  if (q) ans += " — " + q;
                }
                return (
                <tr key={ci} className="border-b border-slate-100">
                  <td className="p-2">{ci + 1}. {label}</td>
                  <td className="w-24 p-2 text-center text-slate-600">{ans}</td>
                  <td className="w-24 p-2 text-center">{df?.fileId ? <button type="button" onClick={() => openFileById(df.fileId)} className="text-[12px] font-semibold text-[#c2570a] underline">कागदपत्र</button> : <span className="text-slate-300">—</span>}</td>
                  <td className="w-16 p-2 text-center text-slate-400">/ {max}</td>
                  <td className={`p-2 ${isExp ? "w-60" : useButtons ? "w-44" : "w-24"}`}>
                    {isExp ? (
                      <div className="flex flex-wrap justify-center gap-1.5">
                        <button type="button" disabled={readOnly} onClick={() => setScore(`s${si}_${ci}`, max, maxBelow)}
                          data-testid={`mark-below-s${si}-c${ci}`}
                          className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold transition-colors ${scores[`s${si}_${ci}`] === maxBelow ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-amber-50"} disabled:opacity-50`}>आतील ({maxBelow})</button>
                        <button type="button" disabled={readOnly} onClick={() => setScore(`s${si}_${ci}`, max, max)}
                          data-testid={`mark-above-s${si}-c${ci}`}
                          className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold transition-colors ${scores[`s${si}_${ci}`] === max ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-emerald-50"} disabled:opacity-50`}>वरील ({max})</button>
                        <button type="button" disabled={readOnly} onClick={() => setScore(`s${si}_${ci}`, max, 0)}
                          data-testid={`mark-none-s${si}-c${ci}`}
                          className={`rounded-full px-2.5 py-1 text-[11.5px] font-bold transition-colors ${scores[`s${si}_${ci}`] === 0 ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-red-50"} disabled:opacity-50`}>नाही</button>
                      </div>
                    ) : useButtons ? (
                      <div className="flex justify-center gap-2">
                        <button type="button" disabled={readOnly} onClick={() => setScore(`s${si}_${ci}`, max, max)}
                          data-testid={`mark-yes-s${si}-c${ci}`}
                          className={`rounded-full px-3.5 py-1 text-[12px] font-bold transition-colors ${scores[`s${si}_${ci}`] === max ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-emerald-50"} disabled:opacity-50`}>होय</button>
                        <button type="button" disabled={readOnly} onClick={() => setScore(`s${si}_${ci}`, max, 0)}
                          data-testid={`mark-no-s${si}-c${ci}`}
                          className={`rounded-full px-3.5 py-1 text-[12px] font-bold transition-colors ${scores[`s${si}_${ci}`] === 0 ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-red-50"} disabled:opacity-50`}>नाही</button>
                      </div>
                    ) : (
                      <input type="number" min={0} max={max} value={scores[`s${si}_${ci}`] ?? ""}
                        onChange={(e) => setScore(`s${si}_${ci}`, max, e.target.value)} disabled={readOnly}
                        data-testid={`mark-s${si}-c${ci}`}
                        className="w-full rounded border border-slate-300 px-2 py-1 text-center text-[13px] outline-none focus:border-[#1b5e3f] disabled:bg-slate-50 disabled:text-slate-500" />
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        );
      })}
      <div className="mb-3 flex items-center justify-between rounded-xl bg-[#f3efe2] px-4 py-3">
        <span className="text-[15px] font-bold text-[#1b3d2a]">{isFull ? "एकूण प्राप्त गुण" : "नेमून दिलेल्या विभागांचे एकूण गुण"}</span>
        <span className="text-[20px] font-bold text-[#1b5e3f]" data-testid="mark-grand-total">{grand} / {roleMax}</span>
      </div>
      <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} placeholder="शेरा (ऐच्छिक)" readOnly={readOnly}
        className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-[13.5px] outline-none focus:border-[#1b5e3f] read-only:bg-slate-50" />
      {!readOnly && (
        <button onClick={save} disabled={busy} data-testid="mark-save"
          className="w-full rounded-full bg-[#1b5e3f] py-3 text-[14px] font-bold text-white hover:bg-[#144d33] disabled:opacity-60">गुण जतन करा</button>
      )}
    </div>
  );
}

export function RdcMarkingTab({ role = "rdc", readOnly = false, tehsils = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [tehsilFilter, setTehsilFilter] = useState("");
  const [lbFilter, setLbFilter] = useState("");
  const secIdx = ROLE_MARK_SECTIONS[role] || ROLE_MARK_SECTIONS.rdc;
  const roleMax = secIdx.reduce((a, si) => {
    const sec = MARKING_SECTIONS[si];
    return a + (sec.cap || sec.items.reduce((x, [, m]) => x + m, 0));
  }, 0);
  const doneOf = (a) => (role === "rdc" ? !!a.markingDone : !!(a.markingParts && a.markingParts[role]));
  const roleScore = (a) => {
    const sc = (a.marking && a.marking.scores) || {};
    return secIdx.reduce((a2, si) => {
      const sec = MARKING_SECTIONS[si];
      let t = sec.items.reduce((x, _, ci) => x + (Number(sc[`s${si}_${ci}`]) || 0), 0);
      return a2 + (sec.cap ? Math.min(sec.cap, t) : t);
    }, 0);
  };
  const load = useCallback(() => {
    setLoading(true);
    api.markingList().then((d) => setRows(d || [])).catch(() => {}).finally(() => { setLoading(false); setOpenId(null); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) {
    return (
      <div>
        <button onClick={() => setOpenId(null)} className="mb-3 rounded-full border border-slate-300 px-4 py-1.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50" data-testid="mark-back">← यादीकडे परत</button>
        <MarkingForm appId={openId} onDone={load} role={role} readOnly={readOnly} />
      </div>
    );
  }

  const tehs = {};
  rows.forEach((a) => {
    const t = a.tehsil || "—";
    tehs[t] = tehs[t] || { total: 0, done: 0 };
    tehs[t].total += 1;
    if (doneOf(a)) tehs[t].done += 1;
  });
  const total = rows.length, done = rows.filter((a) => doneOf(a)).length;
  if (readOnly) {
    const talukaCount = tehsils.length || Object.keys(tehs).length;
    const mstats = [
      { icon: Layers, label: "एकूण पात्र अर्ज", value: total, color: "text-[#1b5e3f]" },
      { icon: CheckCircle2, label: "गुणांकन झालेले", value: done, color: "text-emerald-600" },
      { icon: Clock, label: "गुणांकन प्रलंबीत", value: total - done, color: "text-amber-600" },
      { icon: ClipboardCheck, label: "तालुके", value: talukaCount, color: "text-[#c2570a]" },
    ];
    return <div><StatCards items={mstats} /></div>;
  }
  const tehsilOpts = [...new Set(rows.map((r) => r.tehsil).filter(Boolean))];
  const lbOpts = [...new Set(rows.filter((r) => !tehsilFilter || r.tehsil === tehsilFilter).map((r) => lbName(r)).filter(Boolean))];
  const filtered = rows.filter((r) => (!tehsilFilter || r.tehsil === tehsilFilter) && (!lbFilter || lbName(r) === lbFilter));

  const downloadExcel = () => {
    const head = ["अ.क्र.", "तहसील", "स्थानिक स्वराज्य संस्था", "अर्ज क्र.", "अर्जदाराचे नाव", "प्राप्त गुण"];
    const body = filtered.map((a, i) => [i + 1, TEH_LABEL[a.tehsil] || a.tehsil, lbName(a), a.applicationNumber, applicant(a), doneOf(a) ? `${roleScore(a)}/${roleMax}` : "—"]);
    const html = `<table border="1"><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr>${body.map((r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`).join("")}</table>`;
    const blob = new Blob(["\ufeff<html><head><meta charset='UTF-8'></head><body>" + html + "</body></html>"], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `गुणांकन_${tehsilFilter ? (TEH_LABEL[tehsilFilter] || tehsilFilter) : "सर्व"}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* तहसील कार्यालयनिहाय तक्ता */}
      <div className="mb-5 overflow-x-auto rounded-2xl border border-[#e6dcc4] bg-white p-4">
        <p className="mb-2 text-[14px] font-bold text-[#1b5e3f]">तहसील कार्यालयनिहाय गुणांकन प्रगती</p>
        <table className="w-full text-[13px]">
          <thead><tr className="bg-slate-100 text-left text-slate-600"><th className="p-2">तहसील कार्यालय</th><th className="p-2 text-center">एकूण पात्र अर्ज</th><th className="p-2 text-center">गुणांकन झालेले</th><th className="p-2 text-center">प्रलंबीत</th></tr></thead>
          <tbody>
            {Object.entries(tehs).map(([t, v]) => (
              <tr key={t} className="border-b border-slate-100">
                <td className="p-2 font-semibold text-[#1b3d2a]">{TEH_LABEL[t] || t}</td>
                <td className="p-2 text-center">{v.total}</td>
                <td className="p-2 text-center text-emerald-700">{v.done}</td>
                <td className="p-2 text-center text-amber-700">{v.total - v.done}</td>
              </tr>
            ))}
            {total > 0 && (
              <tr className="border-t-2 border-[#1b5e3f] bg-[#f3efe2] font-bold" data-testid="marking-total-row">
                <td className="p-2 text-[#1b5e3f]">एकूण</td>
                <td className="p-2 text-center">{total}</td>
                <td className="p-2 text-center text-emerald-700">{done}</td>
                <td className="p-2 text-center text-amber-700">{total - done}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <p className="text-[15px] font-bold text-[#1b5e3f]">पात्र अर्जदार — गुणांकन</p>
          {!readOnly && (
            <>
              <select value={tehsilFilter} onChange={(e) => { setTehsilFilter(e.target.value); setLbFilter(""); }} data-testid="marking-filter-tehsil"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f]">
                <option value="">सर्व तहसील</option>
                {tehsilOpts.map((t) => <option key={t} value={t}>{TEH_LABEL[t] || t}</option>)}
              </select>
              <select value={lbFilter} onChange={(e) => setLbFilter(e.target.value)} data-testid="marking-filter-lb"
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-[13px] outline-none focus:border-[#1b5e3f]">
                <option value="">सर्व संस्था</option>
                {lbOpts.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
              <button onClick={downloadExcel} disabled={filtered.length === 0} data-testid="marking-excel"
                className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#1b5e3f] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
                <FileDown className="h-4 w-4" /> Excel डाउनलोड
              </button>
            </>
          )}
        </div>
        {loading ? (<div className="py-8 text-center text-slate-400">लोड होत आहे...</div>) :
          filtered.length === 0 ? (<div className="py-8 text-center text-slate-400" data-testid="mark-empty">गुणांकनासाठी पात्र अर्ज नाहीत.</div>) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="rdc-marking-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">अ.क्र.</th>
                  <th className="p-2.5 font-semibold">तहसील</th>
                  <th className="p-2.5 font-semibold">स्थानिक स्वराज्य संस्था</th>
                  <th className="p-2.5 font-semibold">अर्ज क्र.</th>
                  <th className="p-2.5 font-semibold">अर्जदाराचे नाव</th>
                  <th className="p-2.5 text-center font-semibold">प्राप्त गुण</th>
                  <th className="p-2.5 text-center font-semibold">पहा</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a, i) => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-[#faf8f0]">
                    <td className="p-2.5">{i + 1}</td>
                    <td className="p-2.5">{TEH_LABEL[a.tehsil] || a.tehsil}</td>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{lbName(a)}</td>
                    <td className="p-2.5 font-latin font-semibold text-[#1b3d2a]">{a.applicationNumber}</td>
                    <td className="p-2.5">{applicant(a)}</td>
                    <td className="p-2.5 text-center font-bold">{doneOf(a) ? <span className="text-[#1b5e3f]">{roleScore(a)}/{roleMax}</span> : <span className="text-slate-400">—</span>}</td>
                    <td className="p-2.5 text-center">
                      <button onClick={() => setOpenId(a.id)} data-testid={`mark-open-${a.id}`}
                        className="rounded-full bg-[#c2570a] px-4 py-1.5 text-[12.5px] font-semibold text-white hover:opacity-90">पहा</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


function IvStat({ icon: Icon, value, label, color }) {
  return (
    <div className="rounded-xl border border-[#e6dcc4] bg-white p-4">
      <Icon className={`mb-1.5 h-5 w-5 ${color}`} />
      <p className={`text-[24px] font-bold ${color}`}>{value}</p>
      <p className="text-[12.5px] text-slate-500">{label}</p>
    </div>
  );
}

function RdcInterviewTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.rdcInterviewList().then((d) => setRows(d || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (teh, key, val) => setRows((r) => r.map((x) => (x.tehsil === teh ? { ...x, [key]: val } : x)));

  const confirmPhysical = (teh) =>
    window.confirm(`या तालुक्यातील (${TEH_LABEL[teh] || teh}) सर्व पात्र अर्जांची पायाभूत सुविधा तपासणी पूर्ण झाली आहे का?\n\nखात्री करूनच पुढे जा. प्रत्येक अर्जदारास स्वतंत्र मुलाखत पत्र ईमेलद्वारे पाठवले जाईल.`);

  const validate = (row) => {
    if (!row.date || !row.time) { toast.error("कृपया मुलाखतीचा दिनांक व वेळ निवडा."); return false; }
    if (!row.eligibleCount) { toast.error("या तहसीलमध्ये पात्र अर्जदार नाहीत."); return false; }
    return true;
  };

  const sendLetters = async (row) => {
    if (!validate(row) || !confirmPhysical(row.tehsil)) return;
    setBusy(row.tehsil + ":send");
    try {
      await api.rdcInterviewSlot({ tehsil: row.tehsil, date: row.date, time: row.time });
      const res = await api.rdcInterviewSend({ tehsil: row.tehsil, date: row.date, time: row.time });
      toast.success(`मुलाखत पत्रे पाठवली — ${res.sent} पाठवले${res.skipped ? `, ${res.skipped} वगळले (ई-मेल नाही)` : ""}.`);
      load();
    } catch (e) { toast.error(e.message || "पत्रे पाठवताना त्रुटी."); }
    setBusy("");
  };

  const downloadPdf = async (row) => {
    if (!validate(row) || !confirmPhysical(row.tehsil)) return;
    setBusy(row.tehsil + ":pdf");
    try {
      await api.rdcInterviewSlot({ tehsil: row.tehsil, date: row.date, time: row.time });
      await api.rdcInterviewPdf(row.tehsil, row.date, row.time);
      toast.success("मुलाखत पत्रे PDF डाउनलोड झाली.");
    } catch (e) { toast.error(e.message || "PDF डाउनलोड अयशस्वी."); }
    setBusy("");
  };

  const totalElig = rows.reduce((a, r) => a + (r.eligibleCount || 0), 0);
  const totalSent = rows.reduce((a, r) => a + (r.lettersSent || 0), 0);

  return (
    <div>
      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <IvStat icon={Users} value={totalElig} label="एकूण पात्र अर्जदार" color="text-[#1b5e3f]" />
        <IvStat icon={Mail} value={totalSent} label="मुलाखत पत्रे पाठवली" color="text-emerald-600" />
        <IvStat icon={Clock} value={Math.max(0, totalElig - totalSent)} label="पत्रे प्रलंबीत" color="text-amber-600" />
      </div>

      <div className="rounded-2xl border border-[#e6dcc4] bg-white p-5">
        <p className="mb-1 text-[15px] font-bold text-[#1b5e3f]">मुलाखत पत्र — तहसील कार्यालयनिहाय</p>
        <p className="mb-3 text-[12.5px] text-slate-500">प्रत्येक तहसील कार्यालयासाठी मुलाखतीचा दिनांक व वेळ निवडा, नंतर पात्र अर्जदारांना पत्रे ईमेल करा किंवा एकत्रित PDF डाउनलोड करा. (मुलाखतीचे ठिकाण: जिल्हाधिकारी कार्यालय, सांगली)</p>
        {loading ? (
          <div className="py-8 text-center text-slate-400">लोड होत आहे...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="rdc-interview-table">
              <thead>
                <tr className="bg-[#1b5e3f] text-left text-white">
                  <th className="p-2.5 font-semibold">तहसील कार्यालय</th>
                  <th className="p-2.5 text-center font-semibold">पात्र अर्जदार</th>
                  <th className="p-2.5 text-center font-semibold">पायाभूत तपासणी</th>
                  <th className="p-2.5 text-center font-semibold">पत्रे पाठवली</th>
                  <th className="p-2.5 font-semibold">मुलाखत दिनांक</th>
                  <th className="p-2.5 font-semibold">वेळ</th>
                  <th className="p-2.5 text-center font-semibold">कृती</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.tehsil} className="border-b border-slate-100 align-middle" data-testid={`rdc-interview-row-${row.tehsil}`}>
                    <td className="p-2.5 font-semibold text-[#1b3d2a]">{TEH_LABEL[row.tehsil] || row.tehsil}</td>
                    <td className="p-2.5 text-center font-semibold">{row.eligibleCount}</td>
                    <td className="p-2.5 text-center text-[12px]">
                      {row.visitDone}/{row.eligibleCount}
                      {row.visitPending > 0 && <span className="ml-1 text-amber-600">({row.visitPending} प्रलंबीत)</span>}
                    </td>
                    <td className="p-2.5 text-center">{row.lettersSent}</td>
                    <td className="p-2.5">
                      <input type="date" value={row.date || ""} onChange={(e) => setField(row.tehsil, "date", e.target.value)}
                        data-testid={`rdc-interview-date-${row.tehsil}`}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-[12.5px] outline-none focus:border-[#1b5e3f]" />
                    </td>
                    <td className="p-2.5">
                      <input type="time" value={row.time || ""} onChange={(e) => setField(row.tehsil, "time", e.target.value)}
                        data-testid={`rdc-interview-time-${row.tehsil}`}
                        className="rounded-lg border border-slate-300 px-2 py-1.5 text-[12.5px] outline-none focus:border-[#1b5e3f]" />
                    </td>
                    <td className="p-2.5">
                      <div className="flex flex-col items-stretch gap-1.5">
                        <button onClick={() => sendLetters(row)} disabled={!!busy || !row.eligibleCount} data-testid={`rdc-interview-send-${row.tehsil}`}
                          className="inline-flex items-center justify-center gap-1 rounded-full bg-[#1b5e3f] px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-[#144d33] disabled:opacity-50">
                          <Mail className="h-3.5 w-3.5" /> {busy === row.tehsil + ":send" ? "पाठवत आहे..." : "पत्रे ईमेल करा"}
                        </button>
                        <button onClick={() => downloadPdf(row)} disabled={!!busy || !row.eligibleCount} data-testid={`rdc-interview-pdf-${row.tehsil}`}
                          className="inline-flex items-center justify-center gap-1 rounded-full border border-[#c2570a] px-3 py-1.5 text-[12px] font-semibold text-[#c2570a] hover:bg-[#c2570a]/5 disabled:opacity-50">
                          <Download className="h-3.5 w-3.5" /> {busy === row.tehsil + ":pdf" ? "..." : "पत्रे PDF डाउनलोड"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RdcDashboard({ readOnly = false }) {
  const [view, setView] = useState("dash");
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.officerMeta().then(setMeta).catch(() => {});
  }, []);

  const tehsils = (meta && meta.scopeTehsils) || [];

  const tabBtn = (key, label) => (
    <button onClick={() => setView(key)} data-testid={`rdc-view-${key}`}
      className={`rounded-full px-5 py-2 text-[13.5px] font-semibold transition-colors ${view === key ? "bg-[#1b5e3f] text-white" : "bg-white text-slate-600 ring-1 ring-slate-300 hover:bg-slate-50"}`}>{label}</button>
  );

  return (
    <OfficerShell>
      <div className="mb-5">
        <h1 className="font-head text-[26px] font-medium text-[#1b5e3f]">
          {readOnly ? "जिल्हास्तरीय डॅशबोर्ड (केवळ पाहणे)" : "निवासी उपजिल्हाधिकारी डॅशबोर्ड"}
        </h1>
        <p className="text-[14px] text-slate-500">
          {readOnly
            ? `संपूर्ण जिल्हा · ${meta?.jurisdiction || "सांगली"} · तहसील कार्यालयनिहाय स्थिती (केवळ आकडेवारी)`
            : `क्षेत्र: ${meta?.jurisdiction || "संपूर्ण जिल्हा"} · तहसील कार्यालयनिहाय स्थिती, पडताळणी व अंतिम निर्णय`}
        </p>
      </div>

      <div className="mb-5 flex flex-wrap gap-2">
        {tabBtn("dash", "डॅशबोर्ड")}
        {tabBtn("verify", "प्राथमिक पडताळणी")}
        {!readOnly && tabBtn("final", "अंतिम निर्णय झालेले अर्ज")}
        {tabBtn("physical", "पायाभूत सुविधा तपासणी अहवाल")}
        {tabBtn("marking", "गुणांकन")}
        {!readOnly && tabBtn("interview", "मुलाखत पत्र")}
        {!readOnly && tabBtn("fee", "शुल्क पडताळणी")}
        {tabBtn("result", "निकाल")}
      </div>

      {view === "dash" && <TehsilOfficeDashboard showSdo />}
      {view === "verify" && <RdcReviewTab tehsils={tehsils} readOnly={readOnly} />}
      {view === "final" && !readOnly && <RdcFinalTab tehsils={tehsils} />}
      {view === "physical" && <RdcPhysicalTab tehsils={tehsils} readOnly={readOnly} />}
      {view === "marking" && <RdcMarkingTab readOnly={readOnly} tehsils={tehsils} />}
      {view === "interview" && !readOnly && <RdcInterviewTab />}
      {view === "fee" && !readOnly && <FeePaymentsView mode="rdc" />}
      {view === "result" && <ResultTab canApprove={false} />}
    </OfficerShell>
  );
}
